import { useId, useState } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import { BookOpen, Coins, Loader2, RefreshCw, Trash2, X } from "lucide-react";
import {
  useCreatePatronCategory,
  useDeletePatronCategory,
  useUpdatePatronCategory,
} from "@/api/hooks";
import { ApiClientError } from "@/api/client";
import type { PatronCategory, PatronCategoryRulesInput, PatronCategoryType } from "@/api/types";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { CATEGORY_RULE_SPECS, readDraft, readField, toDraft } from "../../loanRules";
import {
  CATEGORY_TYPE_LABELS,
  CODE_MAX_LENGTH,
  DEFAULT_CATEGORY_RULES,
  codeFromName,
  nextSortOrder,
  pickRules,
  validateIdentity,
  type IdentityErrors,
} from "../../patronCategories";
import { RuleGroup, SwitchField, TextField, UnitField } from "../loan-rules/fields";
import { LibraryCardPreview } from "./LibraryCardPreview";

type RuleField = keyof typeof CATEGORY_RULE_SPECS;

const STANDARD = "";

const TYPE_DESCRIPTIONS: Record<PatronCategoryType, string> = {
  individual: "Une personne, titulaire de sa propre carte.",
  institutional: "Une école, une crèche, une association : un compte pour un groupe.",
};

/**
 * Create a patron category, or edit the identity of an existing one (its
 * rules are edited on the page). Mounted only while open, so each opening
 * starts from the category being edited.
 */
export function CategoryDialog({
  categories,
  category,
  onClose,
  onSaved,
}: {
  categories: PatronCategory[];
  /** null creates a new category */
  category: PatronCategory | null;
  onClose: () => void;
  /** The created or updated category, or null once deleted */
  onSaved: (category: PatronCategory | null) => void;
}) {
  const isNew = category === null;
  const formId = useId();

  const [name, setName] = useState(category?.name ?? "");
  const [code, setCode] = useState(category?.code ?? "");
  // A new code follows the name until the administrator types one
  const [codeEdited, setCodeEdited] = useState(!isNew);
  const [type, setType] = useState<PatronCategoryType>(category?.type ?? "individual");
  const [description, setDescription] = useState(category?.description ?? "");
  const [active, setActive] = useState(category?.active ?? true);
  const [source, setSource] = useState(STANDARD);
  const [rules, setRules] = useState(
    toDraft<RuleField>(category ?? DEFAULT_CATEGORY_RULES, CATEGORY_RULE_SPECS)
  );
  const [identityErrors, setIdentityErrors] = useState<IdentityErrors>({});
  const [ruleErrors, setRuleErrors] = useState<Partial<Record<RuleField, string | undefined>>>({});
  const [serverError, setServerError] = useState<string | null>(null);

  const createCategory = useCreatePatronCategory();
  const updateCategory = useUpdatePatronCategory();
  const isPending = createCategory.isPending || updateCategory.isPending;

  const previewRules: Partial<PatronCategoryRulesInput> = {};
  for (const key of Object.keys(CATEGORY_RULE_SPECS) as RuleField[]) {
    const result = readField(rules[key], CATEGORY_RULE_SPECS[key]);
    if (result.ok) previewRules[key] = result.value;
  }

  const changeName = (value: string) => {
    setName(value);
    if (!codeEdited) setCode(codeFromName(value));
    setIdentityErrors((current) => ({ ...current, name: undefined }));
  };

  const changeSource = (value: string) => {
    setSource(value);
    const from = categories.find((c) => c.id === value);
    setRules(
      toDraft<RuleField>(from ? pickRules(from) : DEFAULT_CATEGORY_RULES, CATEGORY_RULE_SPECS)
    );
    setRuleErrors({});
  };

  const ruleField = (field: RuleField) => ({
    value: rules[field],
    onChange: (value: string) => {
      setRules((current) => ({ ...current, [field]: value }));
      setRuleErrors((current) => ({ ...current, [field]: undefined }));
    },
    error: ruleErrors[field],
  });

  const submit = async () => {
    setServerError(null);
    const nextIdentityErrors = validateIdentity({ name, code }, categories, category?.id ?? null);
    const { values, errors } = isNew
      ? readDraft(rules, CATEGORY_RULE_SPECS)
      : { values: null, errors: {} };
    setIdentityErrors(nextIdentityErrors);
    setRuleErrors(errors);
    if (Object.keys(nextIdentityErrors).length > 0 || (isNew && !values)) return;

    const identity = {
      name: name.trim(),
      code: code.trim().toUpperCase(),
      type,
      description: description.trim() === "" ? null : description.trim(),
      active,
    };

    try {
      const saved =
        isNew && values
          ? await createCategory.mutateAsync({
              ...identity,
              ...values,
              sortOrder: nextSortOrder(categories),
            })
          : await updateCategory.mutateAsync({ id: category?.id ?? "", input: identity });
      onSaved(saved);
    } catch (err) {
      if (err instanceof ApiClientError && err.status === 409) {
        setIdentityErrors({ code: err.message });
        return;
      }
      const violations = err instanceof ApiClientError ? (err.data?.violations ?? []) : [];
      const fieldErrors: IdentityErrors = {};
      for (const violation of violations) {
        if (violation.propertyPath === "name" || violation.propertyPath === "code") {
          fieldErrors[violation.propertyPath] = violation.message;
        }
      }
      if (Object.keys(fieldErrors).length > 0) setIdentityErrors(fieldErrors);
      else setServerError(err instanceof Error ? err.message : "L'enregistrement a échoué");
    }
  };

  return (
    <Dialog.Root
      open
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <Dialog.Portal>
        <Dialog.Overlay className="animate-fade-in fixed inset-0 z-40 bg-gray-950/40 backdrop-blur-sm" />
        <Dialog.Content className="animate-fade-in fixed inset-y-0 right-0 z-50 flex w-full max-w-5xl flex-col bg-white shadow-2xl dark:bg-gray-900">
          <header className="flex items-start justify-between gap-4 border-b border-gray-200 px-6 py-5 dark:border-gray-700">
            <div>
              <Dialog.Title className="text-lg font-semibold text-gray-900 dark:text-white">
                {isNew ? "Nouvelle catégorie de lecteurs" : `Modifier « ${category.name} »`}
              </Dialog.Title>
              <Dialog.Description className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                {isNew
                  ? "Elle sera proposée à l'inscription des lecteurs dès sa création."
                  : "Les règles de prêt se modifient sur la page de la catégorie."}
              </Dialog.Description>
            </div>
            <Dialog.Close asChild>
              <Button variant="ghost" size="icon" aria-label="Fermer">
                <X aria-hidden />
              </Button>
            </Dialog.Close>
          </header>

          <div className="flex min-h-0 flex-1 flex-col overflow-y-auto lg:flex-row lg:overflow-hidden">
            <aside
              aria-label="Aperçu"
              className="border-b border-gray-200 bg-gray-50 px-6 py-6 lg:order-2 lg:w-[24rem] lg:shrink-0 lg:overflow-y-auto lg:border-b-0 lg:border-l dark:border-gray-700 dark:bg-gray-950/40"
            >
              <LibraryCardPreview
                name={name}
                code={code}
                type={type}
                active={active}
                rules={isNew ? previewRules : pickRules(category)}
              />
            </aside>

            <form
              id={formId}
              noValidate
              onSubmit={(event) => {
                event.preventDefault();
                void submit();
              }}
              className="min-w-0 flex-1 space-y-8 px-6 py-6 lg:overflow-y-auto"
            >
              <section aria-labelledby={`${formId}-identity`} className="space-y-5">
                <SectionTitle id={`${formId}-identity`} step={isNew ? 1 : null}>
                  Identité
                </SectionTitle>

                <div className="grid gap-4 sm:grid-cols-[minmax(0,1fr)_11rem]">
                  <TextField
                    label="Nom"
                    value={name}
                    onChange={changeName}
                    error={identityErrors.name}
                    placeholder="ex. Jeune (12-17 ans)"
                    maxLength={100}
                    autoFocus
                  />
                  <TextField
                    label="Code"
                    value={code}
                    onChange={(value) => {
                      setCode(value.toUpperCase());
                      setCodeEdited(true);
                      setIdentityErrors((current) => ({ ...current, code: undefined }));
                    }}
                    error={identityErrors.code}
                    hint={
                      isNew && !codeEdited ? "Proposé d'après le nom" : "Utilisé dans les imports"
                    }
                    maxLength={CODE_MAX_LENGTH}
                    mono
                  />
                </div>

                <fieldset>
                  <legend className="mb-1.5 text-sm text-gray-700 dark:text-gray-200">
                    Type de carte
                  </legend>
                  <div className="grid gap-3 sm:grid-cols-2">
                    {(Object.keys(CATEGORY_TYPE_LABELS) as PatronCategoryType[]).map((option) => (
                      <label
                        key={option}
                        className={cn(
                          "has-focus-visible:ring-ring cursor-pointer rounded-xl border p-4 transition-colors has-focus-visible:ring-2",
                          type === option
                            ? "border-primary/50 bg-primary/5 dark:bg-primary/10"
                            : "border-gray-200 hover:border-gray-300 dark:border-gray-700 dark:hover:border-gray-600"
                        )}
                      >
                        <span className="flex items-center gap-2.5">
                          <input
                            type="radio"
                            name={`${formId}-type`}
                            value={option}
                            checked={type === option}
                            onChange={() => {
                              setType(option);
                            }}
                            className="accent-primary h-4 w-4"
                          />
                          <span className="text-sm font-medium text-gray-900 dark:text-white">
                            {CATEGORY_TYPE_LABELS[option]}
                          </span>
                        </span>
                        <span className="mt-1 block pl-6.5 text-xs text-gray-500 dark:text-gray-400">
                          {TYPE_DESCRIPTIONS[option]}
                        </span>
                      </label>
                    ))}
                  </div>
                </fieldset>

                <div>
                  <label
                    htmlFor={`${formId}-description`}
                    className="mb-1.5 block text-sm text-gray-700 dark:text-gray-200"
                  >
                    Description <span className="text-gray-400">(facultatif)</span>
                  </label>
                  <textarea
                    id={`${formId}-description`}
                    value={description}
                    onChange={(event) => {
                      setDescription(event.target.value);
                    }}
                    rows={2}
                    maxLength={500}
                    placeholder="Qui peut en bénéficier, justificatifs demandés…"
                    className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 dark:border-gray-600 dark:bg-gray-800 dark:text-white"
                  />
                </div>

                {!isNew && (
                  <SwitchField
                    label="Catégorie active"
                    description="Inactive, elle n'est plus proposée à l'inscription ; ses lecteurs la gardent."
                    checked={active}
                    onChange={setActive}
                  />
                )}
              </section>

              {isNew && (
                <section aria-labelledby={`${formId}-rules`} className="space-y-5">
                  <SectionTitle id={`${formId}-rules`} step={2}>
                    Règles de prêt
                  </SectionTitle>

                  <div>
                    <label
                      htmlFor={`${formId}-source`}
                      className="mb-1.5 block text-sm text-gray-700 dark:text-gray-200"
                    >
                      Partir de
                    </label>
                    <select
                      id={`${formId}-source`}
                      value={source}
                      onChange={(event) => {
                        changeSource(event.target.value);
                      }}
                      className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 sm:max-w-sm dark:border-gray-600 dark:bg-gray-800 dark:text-white"
                    >
                      <option value={STANDARD}>Valeurs standard</option>
                      {categories.map((c) => (
                        <option key={c.id} value={c.id}>
                          Copier « {c.name} »
                        </option>
                      ))}
                    </select>
                    <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                      Remplace les valeurs ci-dessous. Les exceptions ne sont pas copiées.
                    </p>
                  </div>

                  <div className="space-y-4">
                    <RuleGroup icon={BookOpen} title="Prêt">
                      <UnitField
                        label="Durée du prêt"
                        unit="jours"
                        {...ruleField("defaultLoanDays")}
                      />
                      <UnitField
                        label="Documents empruntés"
                        unit="max."
                        {...ruleField("maxLoans")}
                      />
                      <UnitField
                        label="Réservations"
                        unit="max."
                        {...ruleField("maxReservations")}
                      />
                    </RuleGroup>
                    <RuleGroup icon={RefreshCw} title="Prolongation">
                      <UnitField
                        label="Prolongations par prêt"
                        unit="max."
                        hint="0 pour interdire"
                        {...ruleField("maxRenewals")}
                      />
                      <UnitField
                        label="Prolongeable dès"
                        unit="j avant"
                        hint="avant l'échéance"
                        {...ruleField("renewalWindowDays")}
                      />
                    </RuleGroup>
                    <RuleGroup icon={Coins} title="Retards">
                      <UnitField
                        label="Amende par jour"
                        unit="€"
                        inputMode="decimal"
                        {...ruleField("finePerDayInCents")}
                      />
                      <UnitField
                        label="Plafond par document"
                        unit="€"
                        inputMode="decimal"
                        hint="0 = sans plafond"
                        {...ruleField("maxFineInCents")}
                      />
                    </RuleGroup>
                  </div>
                </section>
              )}

              {!isNew && (
                <DangerZone
                  category={category}
                  onDeleted={() => {
                    onSaved(null);
                  }}
                />
              )}
            </form>
          </div>

          <footer className="flex flex-wrap items-center justify-end gap-3 border-t border-gray-200 px-6 py-4 dark:border-gray-700">
            {serverError && (
              <p role="alert" className="mr-auto text-sm text-red-700 dark:text-red-400">
                {serverError}
              </p>
            )}
            <Dialog.Close asChild>
              <Button variant="ghost">Annuler</Button>
            </Dialog.Close>
            <Button type="submit" form={formId} disabled={isPending}>
              {isPending && <Loader2 className="animate-spin" aria-hidden />}
              {isNew ? "Créer la catégorie" : "Enregistrer"}
            </Button>
          </footer>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

function SectionTitle({
  id,
  step,
  children,
}: {
  id: string;
  step: number | null;
  children: string;
}) {
  return (
    <h3
      id={id}
      className="flex items-center gap-3 text-sm font-semibold text-gray-900 dark:text-white"
    >
      {step !== null && (
        <span
          aria-hidden
          className="flex h-6 w-6 items-center justify-center rounded-full border border-gray-300 font-mono text-[11px] text-gray-500 dark:border-gray-600 dark:text-gray-400"
        >
          {step}
        </span>
      )}
      {children}
    </h3>
  );
}

/** Deletion asks for an in-place confirmation; the server refuses while patrons belong to the category. */
function DangerZone({ category, onDeleted }: { category: PatronCategory; onDeleted: () => void }) {
  const [confirming, setConfirming] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const deleteCategory = useDeletePatronCategory();

  const remove = async () => {
    setError(null);
    try {
      await deleteCategory.mutateAsync(category.id);
      onDeleted();
    } catch (err) {
      setError(err instanceof Error ? err.message : "La suppression a échoué");
      setConfirming(false);
    }
  };

  return (
    <section
      aria-label="Suppression"
      className="rounded-xl border border-red-200 p-5 dark:border-red-900/60"
    >
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="max-w-md">
          <p className="text-sm font-medium text-gray-900 dark:text-white">
            Supprimer la catégorie
          </p>
          <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
            {confirming
              ? "Ses exceptions de prêt seront supprimées avec elle. Cette action est définitive."
              : "Possible seulement si aucun lecteur n'y est rattaché."}
          </p>
        </div>
        {confirming ? (
          <div className="flex gap-2">
            <Button
              type="button"
              variant="ghost"
              onClick={() => {
                setConfirming(false);
              }}
            >
              Conserver
            </Button>
            <Button
              type="button"
              variant="destructive"
              disabled={deleteCategory.isPending}
              onClick={() => {
                void remove();
              }}
            >
              {deleteCategory.isPending && <Loader2 className="animate-spin" aria-hidden />}
              Supprimer définitivement
            </Button>
          </div>
        ) : (
          <Button
            type="button"
            variant="outline"
            onClick={() => {
              setConfirming(true);
            }}
            className="text-red-700 dark:text-red-400"
          >
            <Trash2 aria-hidden />
            Supprimer…
          </Button>
        )}
      </div>
      {error && (
        <p role="alert" className="mt-3 text-sm text-red-700 dark:text-red-400">
          {error}
        </p>
      )}
    </section>
  );
}
